import {type ModalPage} from '../../express-checkout/ts/models/IEnvironment';

declare global {
	interface ActionCallback {
		'after_modal_open': () => Promise<void> | void;

		'before_billing_page': (fromPage: ModalPage, toPage: ModalPage) => Promise<void> | void;
		'after_billing_page': (fromPage: ModalPage, toPage: ModalPage) => Promise<void> | void;

		'before_shipping_page': (fromPage: ModalPage, toPage: ModalPage) => Promise<void> | void;
		'after_shipping_page': (fromPage: ModalPage, toPage: ModalPage) => Promise<void> | void;

		'before_payment_page': (fromPage: ModalPage, toPage: ModalPage) => Promise<void> | void;
		'after_payment_page': (fromPage: ModalPage, toPage: ModalPage) => Promise<void> | void;
	}
}
type ActionHook = {
	priority: number;
	callback: ActionCallback[keyof ActionCallback];
};
const actionEvents: Record<string, ActionHook[]> = {};

/**
 * Adds an action hook callback.
 *
 * @param name The name of the action hook
 * @param callback The callback to run when the action hook is called
 * @param priority The priority of the callback. Lower numbers are executed first
 * @returns void
 *
 * @example ```ts
 * addAction('example', example => console.log(example));
 * ```
 */
function addAction<T extends keyof ActionCallback>(name: T, callback: ActionCallback[T], priority = 10): void {
	if (!actionEvents[name]) {
		actionEvents[name] = [];
	}

	actionEvents[name]!.push({
		priority,
		callback,
	});

	actionEvents[name]!.sort((a, b) => a.priority - b.priority);
}

/**
 * Runs all callbacks for the given action hook.
 *
 * @param name The name of the action hook
 * @param args The arguments to pass to the callbacks
 * @returns Promise<void>
 *
 * @example ```ts
 * doAction('example', 20).catch(console.error);
 * ```
 */
async function doAction<T extends keyof ActionCallback>(name: T, ...args: Parameters<ActionCallback[T]>): Promise<void> {
	const actionList = actionEvents[name];
	if (!actionList) {
		return;
	}

	for (const action of actionList) {
		// eslint-disable-next-line no-await-in-loop
		await action.callback.apply(null, args);
	}
}

declare global {
	interface FilterCallback {
		'example': (example: number) => Promise<number> | number;
	}
}
type FilterHook = {
	priority: number;
	callback: FilterCallback[keyof FilterCallback];
};
const filterEvents: Record<string, FilterHook[]> = {};

/**
 * Adds a filter hook callback.
 *
 * @param name The name of the filter hook
 * @param callback The callback to run when the filter hook is called
 * @param priority The priority of the callback. Lower numbers are executed first
 *
 * @example ```ts
 * addFilter('example', example => example + 1);
 * ```
 */
function addFilter<T extends keyof FilterCallback>(name: T, callback: FilterCallback[T], priority = 10): void {
	if (!filterEvents[name]) {
		filterEvents[name] = [];
	}

	filterEvents[name]!.push({
		priority,
		callback,
	});

	filterEvents[name]!.sort((a, b) => a.priority - b.priority);
}

/**
 * Runs all callbacks for the given filter hook.
 *
 * @param name The name of the filter hook
 * @param args The arguments to pass to the callbacks
 * @returns The filtered first argument value
 *
 * @example ```ts
 * applyFilters('example', 1).then(console.log).catch(console.error);
 * ```
 */
async function applyFilters<T extends keyof FilterCallback>(name: T, ...args: Parameters<FilterCallback[T]>): Promise<Parameters<FilterCallback[T]>[0]> {
	let value = args[0] as Parameters<FilterCallback[T]>[0];

	const filterList = filterEvents[name];
	if (!filterList) {
		return value;
	}

	for (const filter of filterList) {
		// eslint-disable-next-line no-await-in-loop
		value = await filter.callback.apply(null, args) as Parameters<FilterCallback[T]>[0];
		args[0] = value;
	}

	return value;
}

export {
	type ActionCallback,
	addAction,
	doAction,

	type FilterCallback,
	addFilter,
	applyFilters,
};
